
import React, { useState } from 'react';
import { Heart, Trash2, Pencil, Share2, Sun, CheckCircle2, FileEdit } from 'lucide-react';
import { Phrase, Category } from '../types';
import { copyHtmlToClipboard, copyToClipboard } from '../services/clipboardUtils';

interface PhraseCardProps {
  phrase: Phrase;
  categories?: Category[];
  onEdit: (phrase: Phrase) => void;
  onDelete: (id: string) => void;
  onFavoriteToggle: (id: string) => void;
  onShare: (phrase: Phrase) => void;
  onIA?: (text: string) => void;
  onUse?: (id: string) => void;
  isDarkMode?: boolean;
  isAutoCopied?: boolean;
}

const PhraseCard: React.FC<PhraseCardProps> = ({ 
  phrase, categories = [], onEdit, onDelete, onFavoriteToggle, 
  onShare, onIA, onUse, isDarkMode, isAutoCopied 
}) => {
  const [copied, setCopied] = useState(false);

  React.useEffect(() => {
    if (isAutoCopied) {
      setCopied(true);
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isAutoCopied]);

  const handleCopyAction = async () => {
    const useRichText = localStorage.getItem('modular_copy_rich_text') !== 'false';
    let success = false;
    
    if (useRichText) {
      // Las frases suelen ser texto plano, pero las tratamos como HTML si la opción está activa
      success = await copyHtmlToClipboard(phrase.text);
    } else {
      success = await copyToClipboard(phrase.text);
    }

    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      if (onUse) onUse(phrase.id);
    }
  };

  const getFilterInfo = (filterId: string) => {
    for (const cat of categories) {
      const filter = cat.filters.find(f => f.id === filterId);
      if (filter) {
        const colorBase = cat.color?.split('-')[1] || 'gray';
        const styleClass = isDarkMode
          ? `bg-${colorBase}-900/10 text-${colorBase}-400 border border-${colorBase}-500/20`
          : `bg-${colorBase}-50 text-${colorBase}-600 border border-${colorBase}-100`;
        return { name: filter.name, style: styleClass };
      }
    }
    return null;
  };

  return (
    <div className={`rounded-[28px] shadow-sm p-6 mb-4 relative border transition-all hover:shadow-md ${isDarkMode ? 'bg-[#2a2d34] border-gray-700' : 'bg-white border-slate-100'}`}>
      {/* Indicador de copiado */}
      {copied && (
        <div className="absolute top-4 right-6 flex items-center space-x-1.5 bg-green-500 text-white px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-lg animate-in fade-in zoom-in duration-200 z-10">
          <CheckCircle2 size={10} />
          <span>Copiado</span>
        </div>
      )}

      {/* Área cliqueable para copiar */}
      <div onClick={handleCopyAction} className="cursor-pointer group relative">
        <p className={`font-medium text-[14px] leading-relaxed transition-colors mb-4 ${isDarkMode ? 'text-gray-100 group-hover:text-white' : 'text-slate-800 group-hover:text-blue-600'}`}>
          {phrase.text}
        </p>
      </div>

      {/* Footer con iconos de acción */}
      <div className="flex items-end justify-between mt-2">
        <div className="flex flex-wrap gap-2">
          {phrase.filterIds.slice(0, 3).map(fid => {
            const info = getFilterInfo(fid);
            if (!info) return null;
            return (
              <span key={fid} className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.15em] ${info.style}`}>
                {info.name}
              </span>
            );
          })}
        </div>
        
        <div className="flex items-center space-x-1">
            <button onClick={() => onFavoriteToggle(phrase.id)} className={`p-2 rounded-full transition-colors ${phrase.isFavorite ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-400 hover:bg-slate-50'}`} title="Favorito"><Heart size={18} fill={phrase.isFavorite ? "currentColor" : "none"}/></button>
            <button onClick={() => onShare(phrase)} className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50/10 rounded-full transition-all" title="Compartir"><Share2 size={18} /></button>
            <div className="h-4 w-[1px] bg-gray-200 dark:bg-gray-700 mx-1.5"></div>
            
            <button onClick={() => { onIA?.(phrase.text); onUse?.(phrase.id); }} className="p-2 text-gray-400 hover:text-indigo-500 hover:bg-indigo-50/10 rounded-full transition-all" title="MODULAR-1"><Sun size={18} /></button>
            
            <div className="h-4 w-[1px] bg-gray-200 dark:bg-gray-700 mx-1.5"></div>
            
            <button onClick={() => onEdit(phrase)} className="p-2 text-gray-400 hover:text-slate-600 hover:bg-blue-50/10 rounded-full transition-all" title="Editar"><Pencil size={18} /></button>
            <button onClick={() => onDelete(phrase.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50/10 rounded-full transition-all" title="Eliminar"><Trash2 size={18} /></button>
        </div>
      </div>
    </div>
  );
};

export default PhraseCard;
